import { Link } from '@tanstack/react-router'
import type { BundlePickerTier, Product } from '#bv/data/products'
import { DisplayPrice } from '#bv/components/DisplayPrice'
import { StockAvailabilityBadge } from '#bv/components/StockAvailabilityBadge'
import { tierListingBlurb } from '#bv/lib/bundleTierBlurb'
import { isServerBoostProduct, serverBoostStorefrontFromPriceUsd } from '#bv/lib/serverBoostProduct'

type Props = {
  tiers: BundlePickerTier[]
  catalog: Product[]
  inStock: (productId: number) => boolean
  highlightTierId?: number
  onTierNavigate?: () => void
  /** Compact rows for modal; compare adds delivery snippet on hub PDP. */
  layout?: 'list' | 'compare' | 'modal'
  className?: string
}

function tierProduct(catalog: Product[], productId: number): Product | undefined {
  return catalog.find((p) => p.id === productId)
}

/** Admin overrides live on tier SKUs; always prefer merged catalog child price. */
function tierLivePriceUsd(tier: BundlePickerTier, child: Product | undefined): number {
  if (child && isServerBoostProduct(child)) return serverBoostStorefrontFromPriceUsd(child)
  return child?.price ?? tier.price
}

function tierShowsFromPrice(child: Product | undefined): boolean {
  return Boolean(child && isServerBoostProduct(child))
}

export function BundleTierList({
  tiers,
  catalog,
  inStock,
  highlightTierId,
  onTierNavigate,
  layout = 'list',
  className = '',
}: Props) {
  if (layout === 'modal' || layout === 'list') {
    const modalCards = layout === 'modal'
    return (
      <ul className={`flex flex-col gap-2.5 ${className}`}>
        {tiers.map((tier) => {
          const child = tierProduct(catalog, tier.productId)
          const available = inStock(tier.productId)
          const highlighted = highlightTierId === tier.productId
          const blurb = child ? tierListingBlurb(child, tier.blurb) : tier.blurb

          if (!available) {
            return (
              <li key={tier.productId} id={`tier-${tier.productId}`}>
                <div
                  className={`rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3.5 opacity-60 ${
                    highlighted ? 'ring-1 ring-[rgba(0, 191, 255,0.35)]' : ''
                  } ${modalCards ? 'sm:px-5' : ''}`}
                  aria-disabled
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#7a92b8]">{tier.label}</div>
                      {blurb ? <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[#5a6d8a]">{blurb}</p> : null}
                      <StockAvailabilityBadge inStock={false} size="compact" className="mt-2" />
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-0.5">
                      {tierShowsFromPrice(child) ? (
                        <span className="text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-[#5a6d8a]">
                          From
                        </span>
                      ) : null}
                      <DisplayPrice
                        usd={tierLivePriceUsd(tier, child)}
                        className="font-display text-lg tabular-nums text-[#5a6d8a]"
                      />
                    </div>
                  </div>
                </div>
              </li>
            )
          }

          return (
            <li key={tier.productId} id={`tier-${tier.productId}`}>
              <Link
                to="/products/$productId"
                params={{ productId: String(tier.productId) }}
                onClick={onTierNavigate}
                className={`group block rounded-2xl border text-left transition-all duration-200 ${
                  modalCards ? 'px-4 py-4 sm:px-5 sm:py-4' : 'px-4 py-3'
                } ${
                  highlighted
                    ? 'border-[rgba(0, 191, 255,0.45)] bg-[rgba(0, 191, 255,0.12)] shadow-[0_8px_28px_rgba(0, 191, 255,0.15)]'
                    : 'border-white/[0.1] bg-white/[0.03] hover:border-[rgba(0, 191, 255,0.4)] hover:bg-[rgba(0, 191, 255,0.08)] hover:shadow-[0_8px_28px_rgba(0, 191, 255,0.12)] active:scale-[0.99]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#f0f6ff] transition-colors group-hover:text-white sm:text-base">
                      {tier.label}
                    </div>
                    {blurb ? (
                      <p className={`mt-1 text-[#7a92b8] ${modalCards ? 'line-clamp-3 text-xs leading-relaxed' : 'line-clamp-2 text-xs'}`}>
                        {blurb}
                      </p>
                    ) : null}
                    {modalCards && child?.deliveryInfo ? (
                      <p className="mt-2 line-clamp-2 text-[0.65rem] leading-snug text-[#5a6d8a]">{child.deliveryInfo}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <div className="flex flex-col items-end gap-0.5">
                      {tierShowsFromPrice(child) ? (
                        <span className="text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-[#7a92b8]">
                          From
                        </span>
                      ) : null}
                      <DisplayPrice
                        usd={tierLivePriceUsd(tier, child)}
                        className="font-display text-xl tabular-nums text-[#d4e8ff] sm:text-2xl"
                      />
                    </div>
                    <span
                      className={`btn-primary rounded-full font-semibold ${
                        modalCards ? 'min-h-[2.25rem] px-4 text-xs' : 'min-h-[2rem] px-3 text-[0.65rem]'
                      }`}
                    >
                      View tier →
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    )
  }

  if (layout === 'compare') {
    return (
      <>
        <div className={`flex flex-col gap-2 sm:hidden ${className}`}>
          {tiers.map((tier) => {
            const child = tierProduct(catalog, tier.productId)
            const available = inStock(tier.productId)
            const highlighted = highlightTierId === tier.productId
            const blurb = child ? tierListingBlurb(child, tier.blurb) : tier.blurb

            return (
              <div
                key={tier.productId}
                id={`tier-${tier.productId}`}
                className={`rounded-2xl border px-4 py-3 text-left ${
                  highlighted
                    ? 'border-[rgba(0, 191, 255,0.45)] bg-[rgba(0, 191, 255,0.12)] shadow-[0_8px_28px_rgba(0, 191, 255,0.15)]'
                    : 'border-white/[0.1] bg-white/[0.03]'
                } ${!available ? 'opacity-55' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#f0f6ff]">{tier.label}</div>
                    {blurb ? <p className="mt-1 line-clamp-2 text-xs leading-snug text-[#7a92b8]">{blurb}</p> : null}
                    {child?.deliveryInfo ? (
                      <p className="mt-2 line-clamp-2 text-[0.65rem] leading-snug text-[#5a6d8a]">{child.deliveryInfo}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <div className="flex flex-col items-end gap-0.5">
                      {tierShowsFromPrice(child) ? (
                        <span className="text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-[#7a92b8]">
                          From
                        </span>
                      ) : null}
                      <DisplayPrice usd={tierLivePriceUsd(tier, child)} className="font-display text-lg text-[#d4e8ff]" />
                    </div>
                    <StockAvailabilityBadge inStock={available} size="compact" />
                  </div>
                </div>
                <div className="mt-3">
                  {available ? (
                    <Link
                      to="/products/$productId"
                      params={{ productId: String(tier.productId) }}
                      onClick={onTierNavigate}
                      className="btn-primary min-h-[2rem] w-full rounded-full px-3 text-[0.65rem] font-semibold"
                    >
                      View tier →
                    </Link>
                  ) : (
                    <span className="text-[0.65rem] font-medium text-[#5a6d8a]">Unavailable</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className={`hidden overflow-hidden rounded-xl border border-white/[0.08] sm:block ${className}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[320px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-white/[0.08] bg-black/30 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[#5a6d8a]">
                <th className="px-3 py-2.5 font-bold">Tier</th>
                <th className="px-3 py-2.5 font-bold">Price</th>
                <th className="px-3 py-2.5 font-bold">Stock</th>
                <th className="hidden px-3 py-2.5 font-bold sm:table-cell">Delivery</th>
                <th className="px-3 py-2.5 text-right font-bold" />
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => {
                const child = tierProduct(catalog, tier.productId)
                const available = inStock(tier.productId)
                const highlighted = highlightTierId === tier.productId
                const blurb = child ? tierListingBlurb(child, tier.blurb) : tier.blurb

                return (
                  <tr
                    key={tier.productId}
                    id={`tier-${tier.productId}`}
                    className={`border-b border-white/[0.05] transition-colors last:border-0 ${
                      highlighted ? 'bg-[rgba(0, 191, 255,0.1)]' : 'hover:bg-white/[0.02]'
                    } ${!available ? 'opacity-55' : ''}`}
                  >
                    <td className="px-3 py-3">
                      <div className="font-semibold text-[#f0f6ff]">{tier.label}</div>
                      {blurb ? (
                        <p className="mt-0.5 line-clamp-2 text-[0.65rem] leading-snug text-[#7a92b8]">{blurb}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 tabular-nums">
                      <div className="flex flex-col gap-0.5">
                        {tierShowsFromPrice(child) ? (
                          <span className="text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-[#5a6d8a]">
                            From
                          </span>
                        ) : null}
                        <DisplayPrice usd={tierLivePriceUsd(tier, child)} className="font-display text-sm text-[#d4e8ff]" />
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <StockAvailabilityBadge inStock={available} size="compact" />
                    </td>
                    <td className="hidden max-w-[12rem] px-3 py-3 sm:table-cell">
                      <span className="line-clamp-2 text-[0.65rem] leading-snug text-[#7a92b8]">
                        {child?.deliveryInfo ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      {available ? (
                        <Link
                          to="/products/$productId"
                          params={{ productId: String(tier.productId) }}
                          onClick={onTierNavigate}
                          className="btn-primary min-h-[2rem] rounded-full px-3 text-[0.65rem] font-semibold"
                        >
                          View
                        </Link>
                      ) : (
                        <span className="text-[0.65rem] font-medium text-[#5a6d8a]">Unavailable</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      </>
    )
  }

  return null
}
